import { useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Skeleton,
  Typography,
} from '@mui/material';
import {
  ImageOutlined as ImagePlaceholderIcon,
  Close as CloseIcon,
  ZoomIn as ZoomIcon,
} from '@mui/icons-material';
import { useGetPresignedImageUrlQuery } from '../../../api/gridApi';

type ImageCellProps = {
  screenshotGuid?: string | null;
  title?: string;
  /** Thumbnail width in px. */
  width?: number;
  /** Thumbnail height in px. */
  height?: number;
};

/**
 * Image thumbnail cell — shows a small rectangular, dimmed preview (or an
 * empty-state label when there's no value) with a centered white zoom icon,
 * and opens a full-size lightbox on click.
 *
 * IMPORTANT: never render the raw ScreenshotPath directly — the tracking
 * screenshot bucket is private and only reachable through the backend's
 * presigned-URL resolver. Always go through useGetPresignedImageUrlQuery.
 */
export const ImageCell = ({ screenshotGuid, title, width = 56, height = 32 }: ImageCellProps) => {
  const [open, setOpen] = useState(false);

  const {
    data: presignedUrl,
    isLoading,
    isError,
  } = useGetPresignedImageUrlQuery(screenshotGuid ?? '', {
    skip: !screenshotGuid,
  });

  const openLightbox = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
    setOpen(true);
  };

  if (!screenshotGuid) {
    // No distinction yet between "never had a screenshot" and "was deleted" —
    // PathToScreenshot is empty in both cases, so we show the same label for now.
    return (
      <Typography variant="body2" color="text.secondary" noWrap>
        Screenshot removed
      </Typography>
    );
  }

  if (isLoading) {
    return <Skeleton variant="rounded" width={width} height={height} />;
  }

  if (isError || !presignedUrl) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width,
          height,
          borderRadius: 0.5,
          backgroundColor: 'action.hover',
          color: 'error.main',
        }}
        title="Failed to load preview"
      >
        <ImagePlaceholderIcon fontSize="small" />
      </Box>
    );
  }

  return (
    <>
      <Box
        role="button"
        tabIndex={0}
        onClick={openLightbox}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openLightbox(e);
          }
        }}
        aria-label={title ? `Open screenshot: ${title}` : 'Open screenshot preview'}
        sx={{
          position: 'relative',
          width,
          height,
          borderRadius: 0.5,
          overflow: 'hidden',
          cursor: 'pointer',
          display: 'block',
          '&:hover .image-cell-overlay': {
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
          },
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: '2px',
          },
        }}
      >
        <Box
          component="img"
          src={presignedUrl}
          loading="lazy"
          decoding="async"
          alt=""
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block',
          }}
        />

        {/* Dimming overlay + white zoom icon — always on, so the cell reads
            as "click to expand" rather than a plain photo. */}
        <Box
          className="image-cell-overlay"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
            transition: 'background-color 0.15s ease',
            pointerEvents: 'none',
          }}
        >
          <ZoomIcon fontSize="small" sx={{ color: '#ffffff !important' }} aria-hidden="true" />
        </Box>
      </Box>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        fullWidth
        onClick={(e) => e.stopPropagation()}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            py: 1.5,
            px: 2,
          }}
        >
          {title ?? 'Screenshot'}
          <IconButton aria-label="Close" onClick={() => setOpen(false)} size="small" sx={{ ml: 1 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
            minHeight: 300,
            backgroundColor: 'grey.50',
          }}
        >
          <Box
            component="img"
            src={presignedUrl}
            alt="Screenshot"
            sx={{ maxWidth: '100%', maxHeight: '75vh', objectFit: 'contain' }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};
